<script lang="ts">
	import { ControlsContainer, ListItem, Scroll, Stack, Text } from '@Shelly/shelly-design-system';
	import { capFirstChar } from 'src/helpers/utilities';
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { get } from 'svelte/store';
	import {
		getEvents,
		getTagsAndDeviceChannel,
		parseEventLogData,
	} from '../../../EventLog/event_log_helper';
	import { eventLogStore } from 'src/stores/features/event-log';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	let className = 'mb-s';
	export { className as class };
	export let device;
	export let settings = {};

	let lastEvents = [];

	const getAllEventsForDevice = async () => {
		const [tags, deviceChannel] = getTagsAndDeviceChannel(device, []);
		const res = await getEvents(false, null, tags, 5);
		let fetchedEvents = await parseEventLogData(res, deviceChannel).slice(0, 5);

		// Sort merged events by raw timestamp in descending order
		fetchedEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

		// Limit to 5 events
		lastEvents = fetchedEvents.slice(0, 5);

		// Format each event for radar display
		lastEvents = lastEvents.map((event) => {
			const date = new Date(event.timestamp);
			const year = date.getFullYear();
			const currentYear = new Date().getFullYear();
			const formattedDate = date.toLocaleString('en-GB', {
				day: '2-digit',
				month: 'short',
				...(year !== currentYear && { year: 'numeric' }),
				hour: '2-digit',
				minute: '2-digit',
				hour12: false,
			});

			// Radar-specific event handling
			const f = get(format);
			let title = f('presence_event_unknown', { default: 'Unknown Event' });
			let icon = 'fas fa-radar';

			// Handle radar presence events (eventID: 17)
			if (event.eventID === 17 && event.data?.module) {
				const moduleData = event.data.module;
				const moduleValue = moduleData.value;
				const moduleName = moduleData.name || '';

				// The module name/value are localized, so prefer the locale-independent
				// fields and only fall back to parsing the label for older cached events.
				const zoneMatch = moduleName.match(/zone\s+(\d+)/i);
				const zoneId = event.zoneId ?? (zoneMatch ? zoneMatch[1] : null);
				const presenceState = event.presenceState ?? moduleValue;

				// Get zone name from settings
				let zoneName = null;
				if (zoneId && settings) {
					const zoneKey = `presencezone:${zoneId}`;
					const zoneSettings = settings[zoneKey];
					zoneName = zoneSettings?.name;
				}

				if (presenceState === 'presence') {
					title = zoneName
						? f('presence_event_presence_zone', {
								values: { zoneName },
								default: `Presence ${zoneName}`,
							})
						: zoneId
							? f('presence_event_presence_zone_id', {
									values: { zoneId },
									default: `Presence Zone ${zoneId}`,
								})
							: f('presence_event_presence_detected', { default: 'Presence Detected' });
					icon = 'fas fa-person-simple';
				} else if (presenceState === 'no_presence') {
					title = zoneName
						? f('presence_event_cleared_zone', {
								values: { zoneName },
								default: `Cleared ${zoneName}`,
							})
						: zoneId
							? f('presence_event_cleared_zone_id', {
									values: { zoneId },
									default: `Cleared Zone ${zoneId}`,
								})
							: f('presence_event_presence_cleared', { default: 'Presence Cleared' });
					icon = 'far fa-eye-slash';
				} else {
					// Fallback to the module value
					title = capFirstChar(moduleValue.replace(/_/g, ' '));
					icon = event.icon?.icon || 'fas fa-radar';
				}
			}
			// Handle legacy radar presence events
			else if (event.data?.presencezone) {
				const zoneData = event.data.presencezone;
				if (typeof zoneData === 'object') {
					// Check for presence detection
					if (zoneData.value === true || zoneData.value === 1) {
						title = f('presence_event_presence_detected', { default: 'Presence Detected' });
						icon = 'fas fa-person-simple';
					} else if (zoneData.value === false || zoneData.value === 0) {
						title = f('presence_event_presence_cleared', { default: 'Presence Cleared' });
						icon = 'fas fa-person-simple-off';
					}

					// Add zone information if available
					if (zoneData.id) {
						title += f('presence_event_zone_suffix', {
							values: { id: zoneData.id },
							default: ` (Zone ${zoneData.id})`,
						});
					}
				}
			}
			// Handle general radar/motion events
			else if (event.data?.motion?.value !== undefined) {
				if (event.data.motion.value) {
					title = f('presence_event_motion_detected', { default: 'Motion Detected' });
					icon = 'fas fa-running';
				} else {
					title = f('presence_event_motion_cleared', { default: 'Motion Cleared' });
					icon = 'fas fa-walking';
				}
			}
			// Handle illuminance events
			else if (event.data?.illuminance) {
				title = f('presence_event_illuminance', {
					values: { value: capFirstChar(event.data.illuminance.value || 'Unknown') },
					default: `Illuminance: ${capFirstChar(event.data.illuminance.value || 'Unknown')}`,
				});
				icon =
					event.data.illuminance.value === 'dark'
						? 'fas fa-moon'
						: event.data.illuminance.value === 'twilight'
							? 'fas fa-cloud-sun'
							: 'fas fa-sun';
			}
			// Use existing event name and icon if available
			else if (event.name) {
				title = capFirstChar(event.name.replace(/_/g, ' '));
				icon = event.icon?.icon || 'fas fa-radar';
			}
			// Fallback for other radar events
			else if (event.text) {
				title = capFirstChar(event.text);
			}

			return {
				...event,
				icon: icon,
				title: title,
				timestamp: formattedDate,
				originalTimestamp: event.timestamp,
			};
		});
	};

	onMount(() => {
		getAllEventsForDevice();
		eventLogStore.deviceId = device.id;
	});

	onDestroy(() => {
		eventLogStore.clear();
		unsubscribe();
	});

	const unsubscribe = eventLogStore.getAllEvents(async (newEvents) => {
		// Refresh events when new events are received
		getAllEventsForDevice();
	});

	const dispatch = createEventDispatcher();
</script>

{#if lastEvents?.length}
	{#if $showNewLayout}
		<Text styleType="label-large" class="text-center mb-s">
			{capFirstChar(
				$format('last_events_heading', {
					default: 'Recent Activity',
				}),
			)}
		</Text>
		<Stack gap="xs">
			{#each lastEvents as listItem}
				<ListItem
					icon={listItem?.icon}
					title={listItem?.title}
					actionBtn
					listItemBgColor="--color-0"
					viewType="fullColored"
					on:click={() => {
						dispatch('click', listItem);
					}}
					progress={{
						percent: 0,
						title: listItem?.timestamp,
						showPercentBar: false,
					}}
				/>
			{/each}
		</Stack>
	{:else}
		<ControlsContainer
			smallerScroll
			class={className}
			size="s"
			color={$showNewLayout ? 'color-1' : undefined}
		>
			<svelte:fragment slot="first">
				<Text element="p" class="text-bold mb-s">
					{capFirstChar(
						$format('last_events_heading', {
							default: 'Recent Activity',
						}),
					)}
				</Text>
				<Scroll smallerScroll>
					<Stack gap="xs">
						{#each lastEvents as listItem}
							<ListItem
								icon={listItem?.icon}
								title={listItem?.title}
								actionBtn
								on:click={() => {
									dispatch('click', listItem);
								}}
								progress={{
									percent: 0,
									title: listItem?.timestamp,
									showPercentBar: false,
								}}
							/>
						{/each}
					</Stack>
				</Scroll>
			</svelte:fragment>
		</ControlsContainer>
	{/if}
{/if}
